// Author-written topics for Wisdom writings.
//
// The essays themselves live in src/lib/content.ts; their topic vocabulary is
// editorial and is maintained in the database (wisdom_posts.tags) through the
// admin editor. Topics are never derived from prose — an untagged post simply
// has no topic edges.

import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import { z } from "zod";
import type { Database } from "@/integrations/supabase/types";

function publicClient() {
  const key = process.env.SUPABASE_PUBLISHABLE_KEY!;
  return createClient<Database>(process.env.SUPABASE_URL!, key, {
    auth: { persistSession: false, autoRefreshToken: false },
    global: {
      fetch: (input, init) => {
        const h = new Headers(init?.headers);
        if (key.startsWith("sb_") && h.get("Authorization") === `Bearer ${key}`) h.delete("Authorization");
        h.set("apikey", key);
        return fetch(input, { ...init, headers: h });
      },
    },
  });
}

export const getWisdomTags = createServerFn({ method: "GET" })
  .inputValidator((d: { slug: string }) => z.object({ slug: z.string().min(1) }).parse(d))
  .handler(async ({ data }): Promise<string[]> => {
    const supabase = publicClient();
    const { data: row, error } = await supabase
      .from("wisdom_posts")
      .select("tags")
      .eq("slug", data.slug)
      .eq("published", true)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return (row?.tags ?? []) as string[];
  });
